import { z } from 'zod';

import { Currency } from '../enums/payments';
import {
  FinanceEntryDirection,
  FinanceEntryRecurrence,
} from '../enums/financeEntries';
import { addCivilDays, addCivilMonths, isRealCivilDay } from '../utils/date';
import { CivilDateDto } from './civilDate';
import { IanaTimeZoneDto, isIanaTimeZone } from './timeZone';

/**
 * Nivo 4 / T14 — the manual finance entry wire contract (spec §4.1, §4.6).
 *
 * Three things this file is responsible for, and nothing else:
 *
 *  1. SHAPE. A one-off (`recurrence = NONE`) is a single day and has no end;
 *     a monthly SEGMENT starts on a canonical first-of-month and either runs
 *     open-ended or ends on a last-of-month at or after its start. Postgres
 *     enforces the same two rules (`chk_fe_one_off_no_end`,
 *     `chk_fe_monthly_canonical`) — this is the layer that answers 422
 *     instead of 500.
 *  2. IMMUTABILITY. `direction` and `recurrence` are creation-time facts, so
 *     `UpdateFinanceEntryDto` does not list them: `.strict()` turns an update
 *     that mentions either into a refusal rather than a silent no-op.
 *  3. SCOPE. Tenant, organization and actor are derived from the authorized
 *     request. `.strict()` everywhere is what makes a body that carries
 *     `organizationId`/`tenantId` a validation error instead of an
 *     unnoticed, ignored field.
 *
 * What this file deliberately does NOT do: check that `amount` has an exact
 * minor-unit representation. That depends on the currency's minor exponent
 * and lives in ONE place — `FinanceEntriesService`, through `isExactMinor` —
 * so `15.005` comes back as a readable "not exact" message instead of a
 * generic field error. Amounts never travel as minor units on the way IN
 * (the client types major units); every response carries `amountMinor`.
 */

/** Longest note the column and the form accept (spec §4.1: text, nullable). */
export const FINANCE_ENTRY_NOTE_MAX_LENGTH = 4000;

/** List page window (spec §4.6: default 50, maximum 100). */
export const FINANCE_ENTRIES_PAGE_SIZE_DEFAULT = 50;
export const FINANCE_ENTRIES_PAGE_SIZE_MAX = 100;

/** The canonical start of a monthly segment: the first day of its month. */
export function isFirstOfCivilMonth(civil: string): boolean {
  return isRealCivilDay(civil) && civil.endsWith('-01');
}

/**
 * The canonical end of a monthly segment: the last day of its own month,
 * derived (never hard-coded as 28/30/31) so February — leap or not — is
 * right without a special case.
 */
export function isLastOfCivilMonth(civil: string): boolean {
  if (!isRealCivilDay(civil)) return false;
  const firstOfThatMonth = `${civil.slice(0, 7)}-01`;
  return civil === addCivilDays(addCivilMonths(firstOfThatMonth, 1), -1);
}

/** A bare civil day on a query string — never a legacy instant, because a
 *  filter boundary has no picker behind it to have serialized one. */
const financeEntriesQueryDay = z
  .string()
  .refine(isRealCivilDay, { message: 'VALIDATION__InvalidDate' });

/**
 * The one/monthly shape rule, applied to whatever combination of
 * `recurrence`, `occurredOn` and `endsOn` a CREATE carries. An update's
 * shape is checked in the service instead: only the stored row knows the
 * recurrence, which an update may not restate.
 */
function checkRecurrenceShape(
  value: {
    endsOn?: null | string;
    occurredOn: string;
    recurrence: FinanceEntryRecurrence;
  },
  ctx: z.RefinementCtx,
) {
  if (value.recurrence === FinanceEntryRecurrence.NONE) {
    // an explicit null is the same statement as "absent" and stays allowed
    if (value.endsOn != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__FieldNotAllowed',
        path: ['endsOn'],
      });
    }
    return;
  }

  if (!isFirstOfCivilMonth(value.occurredOn)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'VALIDATION__InvalidDate',
      path: ['occurredOn'],
    });
  }
  if (value.endsOn == null) return;
  if (!isLastOfCivilMonth(value.endsOn)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'VALIDATION__InvalidDate',
      path: ['endsOn'],
    });
    return;
  }
  // civil days compare lexicographically; a segment may cover ONE month
  // (start and end inside it), never fewer
  if (value.endsOn < value.occurredOn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'VALIDATION__EndBeforeStart',
      path: ['endsOn'],
    });
  }
}

/**
 * Task 2.1 (spec: "gross is mandatory everywhere; net is optional") — the
 * owner's own "what I really paid/got" beside the mandatory gross. It is
 * checked against the SAME entry's gross here (both travel in one CREATE
 * body); an UPDATE cannot do the same check statically because either side
 * may be omitted — `FinanceEntriesService` compares the EFFECTIVE values
 * after loading the stored row.
 */
function checkNetAmount(
  value: { amount: number; netAmount?: null | number },
  ctx: z.RefinementCtx,
) {
  if (value.netAmount == null) return;
  if (value.netAmount > value.amount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      // The `VALIDATION__*` convention every other custom issue in this file
      // (and every other `@mfw-common/dtos/*`) uses: the global `ZodFilter`
      // looks up `VALIDATION__${message}` in `@mfw/translations`
      // validationMessages, so a bare domain-style id here would resolve to
      // "Unknown, message could not be translated" instead of a real string
      // (code-review finding #1, 2026-09-27).
      message: 'VALIDATION__NetAboveGross',
      path: ['netAmount'],
    });
  }
}

export const CreateFinanceEntryDto = z
  .object({
    direction: z.nativeEnum(FinanceEntryDirection),
    categoryTagId: z.string().uuid(),
    /** major units as the owner typed them; the server converts with
     *  `toMinorUnits` after `isExactMinor` has approved the precision */
    amount: z
      .number()
      .finite('VALIDATION__ValueMustBeANumber')
      .positive('VALIDATION__Positive'),
    currency: z.nativeEnum(Currency),
    recurrence: z.nativeEnum(FinanceEntryRecurrence),
    occurredOn: CivilDateDto,
    endsOn: CivilDateDto.nullable().optional(),
    note: z.string().max(FINANCE_ENTRY_NOTE_MAX_LENGTH).nullable().optional(),
    /** informational only — never enters `netResult` (spec Task 2.1). Same
     *  translated messages as `amount` (code-review finding, 2026-09-27):
     *  a non-finite/non-positive net must read the same way a non-finite/
     *  non-positive gross does, not a generic zod default. */
    netAmount: z
      .number()
      .finite('VALIDATION__ValueMustBeANumber')
      .positive('VALIDATION__Positive')
      .nullable()
      .optional(),
  })
  .strict()
  .superRefine(checkRecurrenceShape)
  .superRefine(checkNetAmount);
export type ApiCreateFinanceEntryDto = z.infer<typeof CreateFinanceEntryDto>;

/**
 * T15 (spec §4.1 "monthly changes preserve closed months") — what a PATCH of
 * a recurring entry is asking for. A running series has months that are
 * already history, so overwriting the row is never the answer; the caller
 * has to say which of the two things it wants:
 *
 *  - `edit` — from this month on, the series costs/earns something else. A
 *    segment that started in a closed month is closed at the previous
 *    month's end and its successor opens at the first of this one.
 *  - `stop` — the series ends with the previous month. That is NOT a delete:
 *    every month it already covered keeps contributing.
 *
 * Deleting a series entered by mistake is the DELETE route with
 * `scope=entire-series`, which removes its historical contributions on
 * purpose.
 */
export const FINANCE_ENTRY_OPERATIONS = ['edit', 'stop'] as const;
export type FinanceEntryOperation = (typeof FINANCE_ENTRY_OPERATIONS)[number];

/** Everything a `stop` must NOT restate: it ends a series, it does not
 *  re-price one, and a body stating both intentions would drop one. */
const ECONOMIC_UPDATE_FIELDS = [
  'amount',
  'categoryTagId',
  'currency',
  'endsOn',
  'netAmount',
  'note',
  'occurredOn',
] as const;

/**
 * A mutation of ONE stored row. `version` is the optimistic-concurrency
 * token the caller last read — mandatory, because two owners editing the
 * same month must not silently overwrite one another (spec §6: stale
 * version → 409).
 *
 * `operation` is required for a MONTHLY target and refused for a one-off;
 * only the stored row knows which it is, so that pairing is checked in
 * `FinanceEntriesService` — this layer checks the shape of the body alone.
 */
export const UpdateFinanceEntryDto = z
  .object({
    version: z
      .number()
      .int('VALIDATION__WholeNumber')
      .positive('VALIDATION__Positive'),
    operation: z.enum(FINANCE_ENTRY_OPERATIONS).optional(),
    categoryTagId: z.string().uuid().optional(),
    amount: z
      .number()
      .finite('VALIDATION__ValueMustBeANumber')
      .positive('VALIDATION__Positive')
      .optional(),
    currency: z.nativeEnum(Currency).optional(),
    occurredOn: CivilDateDto.optional(),
    endsOn: CivilDateDto.nullable().optional(),
    note: z.string().max(FINANCE_ENTRY_NOTE_MAX_LENGTH).nullable().optional(),
    /** informational only — never enters `netResult`. Whether it may be
     *  omitted, cleared or restated is judged against the STORED row, so the
     *  static "net ≤ gross" check happens in `FinanceEntriesService`, not
     *  here (spec Task 2.1). Same translated messages as `amount`. */
    netAmount: z
      .number()
      .finite('VALIDATION__ValueMustBeANumber')
      .positive('VALIDATION__Positive')
      .nullable()
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.operation === 'stop') {
      for (const field of ECONOMIC_UPDATE_FIELDS) {
        if (value[field] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'VALIDATION__FieldNotAllowed',
            path: [field],
          });
        }
      }
      return;
    }
    // Changing the currency LABEL alone would silently re-denominate the
    // stored minor amount (`1000` = €10.00 today, RSD 10.00 after), so the
    // number has to be restated with it.
    if (value.currency !== undefined && value.amount === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__Required',
        path: ['amount'],
      });
    }
  });
export type ApiUpdateFinanceEntryDto = z.infer<typeof UpdateFinanceEntryDto>;

/**
 * DELETE carries its precondition on the query string, because a DELETE has
 * no body: `version` is the same optimistic-concurrency token a PATCH
 * submits, and `scope` is the explicit confirmation that removing a
 * RECURRING entry removes every month it covers (spec §4.1 — a plain delete
 * must never erase a series' history by accident).
 */
export const DeleteFinanceEntryQueryDto = z
  .object({
    version: z.coerce
      .number()
      .int('VALIDATION__WholeNumber')
      .positive('VALIDATION__Positive'),
    scope: z.literal('entire-series').optional(),
  })
  .strict();
export type ApiDeleteFinanceEntryQueryDto = z.infer<
  typeof DeleteFinanceEntryQueryDto
>;

/**
 * The list filter. `from`/`to` is a half-open civil window `[from, to)` and
 * selects everything whose ACTIVE RANGE OVERLAPS it (spec §4.6) — a monthly
 * series that started years ago and never ended belongs in every window it
 * covers, not only in the one its start day falls into.
 */
export const FinanceEntriesQueryDto = z
  .object({
    currency: z.nativeEnum(Currency).optional(),
    direction: z.nativeEnum(FinanceEntryDirection).optional(),
    categoryTagId: z.string().uuid().optional(),
    recurrence: z.nativeEnum(FinanceEntryRecurrence).optional(),
    from: financeEntriesQueryDay.optional(),
    to: financeEntriesQueryDay.optional(),
    page: z.coerce.number().int('VALIDATION__WholeNumber').min(1).default(1),
    pageSize: z.coerce
      .number()
      .int('VALIDATION__WholeNumber')
      .min(1)
      .max(FINANCE_ENTRIES_PAGE_SIZE_MAX)
      .default(FINANCE_ENTRIES_PAGE_SIZE_DEFAULT),
  })
  .strict()
  // A half-open window needs BOTH ends: one alone is not a narrower window
  // but an open-ended one, and the statement it would describe is a
  // different question (§4.5 asks for a month range explicitly).
  .refine((query) => (query.from === undefined) === (query.to === undefined), {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  })
  .refine((query) => !query.from || !query.to || query.from < query.to, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  });
export type ApiFinanceEntriesQueryDto = z.infer<typeof FinanceEntriesQueryDto>;

export { IanaTimeZoneDto, isIanaTimeZone };

/** The longest statement a single request may ask for (spec §4.5). */
export const FINANCE_NET_MAX_MONTHS = 120;

/** Whole calendar months only: `[from, to)` are both first-of-month civil
 *  days, so a month is never half-selected (spec §4.5). */
const financeNetMonthStart = z
  .string()
  .refine(isFirstOfCivilMonth, { message: 'VALIDATION__InvalidDate' });

/**
 * How many whole months `[from, to)` spans. Counted by stepping with
 * `addCivilMonths` rather than by differencing timestamps — months are not a
 * fixed number of milliseconds — and stopped one past the cap, because the
 * only thing the caller needs to know is whether the range is too long.
 */
export function civilMonthsInRange(from: string, to: string): number {
  let months = 0;
  let cursor = from;
  while (cursor < to && months <= FINANCE_NET_MAX_MONTHS) {
    cursor = addCivilMonths(cursor, 1);
    months += 1;
  }
  return months;
}

/**
 * The monthly net statement's window (spec §4.5/§4.6). `.strict()` is what
 * makes an attempted `organizationId`/`tenantId` override a refusal: the
 * scope comes from the authorized path and the session, never from the query
 * string.
 */
export const FinanceNetQueryDto = z
  .object({
    from: financeNetMonthStart,
    to: financeNetMonthStart,
    /** Accepted and IGNORED (Foundation 1 D7): the statement is cut in the
     *  organization's zone. Kept one release so a stale browser tab that
     *  still sends it does not 400; remove it after that. */
    tz: IanaTimeZoneDto.default('UTC'),
  })
  .strict()
  .refine((query) => query.from < query.to, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  })
  .refine(
    (query) =>
      query.from >= query.to ||
      civilMonthsInRange(query.from, query.to) <= FINANCE_NET_MAX_MONTHS,
    { message: 'VALIDATION__InvalidRange', path: ['to'] },
  );
export type ApiFinanceNetQueryDto = z.infer<typeof FinanceNetQueryDto>;

/**
 * Task 5.2 — the window of the category totals (design 2c). The same whole
 * civil months as the net statement, and deliberately nothing else: the
 * totals have to reconcile with `net-result` for the same `[from, to)`, so
 * the two windows are read by one rule. `.strict()` makes a smuggled
 * `organizationId`/`tenantId` a refusal, never an override.
 */
export const FinanceCategoryTotalsQueryDto = z
  .object({
    from: financeNetMonthStart,
    to: financeNetMonthStart,
  })
  .strict()
  .refine((query) => query.from < query.to, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  })
  .refine(
    (query) =>
      query.from >= query.to ||
      civilMonthsInRange(query.from, query.to) <= FINANCE_NET_MAX_MONTHS,
    { message: 'VALIDATION__InvalidRange', path: ['to'] },
  );
export type ApiFinanceCategoryTotalsQueryDto = z.infer<
  typeof FinanceCategoryTotalsQueryDto
>;
